import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

// Finkit brand green (noa --primary / #8fbd56).
const BRAND = '#8fbd56';

export interface LineChartSeries {
  label?: string;
  data: (number | null)[];
  color?: string;
  // Active-dataset emphasis (legacy risk-dashboard-kpi._stylePnlChart): the focused
  // series is drawn thicker (2.5) and on top (order 0); others thinner (1.5) below.
  emphasis?: boolean;
}

interface LineChartProps {
  labels: string[];
  series: LineChartSeries[];
  height?: number | string;
  // Chart.js option overrides merged over the defaults.
  options?: ChartOptions<'line'>;
  // When there's no data, still draw the axes/grid as a placeholder frame, with
  // this hint centered over it (so it's clear a chart belongs here).
  emptyHint?: ReactNode;
}

// 12%-alpha solid fill of a hex colour (legacy _withAlpha(hex, 0.12)).
function withAlpha(hex: string, alpha: number): string {
  const h = hex.charAt(0) === '#' ? hex.slice(0, 7) : BRAND;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return h;
  return `rgba(${r},${g},${b},${alpha})`;
}

// Thin, reusable Chart.js line chart matching the legacy Chart component's
// defaults (responsive, no legend, smooth line, hidden points).
export function LineChart({ labels, series, height = 240, options, emptyHint }: LineChartProps) {
  const hasEmphasis = series.some((s) => s.emphasis);
  const isEmpty = !series.some((s) => s.data.some((v) => v != null));
  const data = useMemo<ChartData<'line'>>(
    () => ({
      labels,
      datasets: series.map((s) => {
        const hex = s.color ?? BRAND;
        const focus = !!s.emphasis;
        return {
          label: s.label ?? '',
          data: s.data,
          borderColor: hex,
          backgroundColor: withAlpha(hex, 0.12),
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          pointHoverRadius: 5,
          // Match legacy emphasis only when a focus dataset is declared; otherwise keep
          // the previous uniform 2px line so other chart consumers are unaffected.
          borderWidth: hasEmphasis ? (focus ? 2.5 : 1.5) : 2,
          order: hasEmphasis ? (focus ? 0 : 1) : 0,
        };
      }),
    }),
    [labels, series, hasEmphasis],
  );

  const mergedOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: !isEmpty } },
      // Empty => draw a clean grid frame (lines only, no tick numbers) as a placeholder.
      scales: isEmpty
        ? {
            x: { grid: { display: true, color: 'rgba(0,0,0,0.05)' }, ticks: { display: false } },
            y: { suggestedMin: 0, suggestedMax: 100, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { display: false } },
          }
        : {
            x: { grid: { display: false } },
            y: { grid: { color: 'rgba(0,0,0,0.05)' } },
          },
      ...options,
    }),
    [options, isEmpty],
  );

  return (
    <div style={{ height, position: 'relative' }}>
      <Line data={data} options={mergedOptions} />
      {isEmpty && emptyHint != null && (
        <div
          className="text-muted small"
          style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
        >
          {emptyHint}
        </div>
      )}
    </div>
  );
}
